/**
 * Subscription API Layer — MangoTV
 * Pattern: mirrors GloTV subscriptionApis.js
 *
 * All subscription data flows through CRM API:
 *   1. fetchPackages()        — GET /products/ (available plans)
 *   2. purchasePackage()      — POST /purchase/ (initiate subscription)
 *   3. fetchPurchaseHistory() — GET /orders/ (billing history)
 *   4. cancelSubscription()   — POST /purchase/?method=delete + update
 *
 * CRM API base: https://tvanywhereonline.com/cm/api
 * Auth: Password + Username headers (from apiConfig)
 */

import axios from "axios";
import { getClientApiUrl, getCachedLandingPage, fetchAndCacheLandingPage, crmRequest } from "../config/apiConfig";
import { DEFAULT_OPERATOR } from "../../utils/operators";
import { checkActiveSubscription } from "../../utils/activeSubs";
import store from "./store";
import { fetchProfile } from "./account";
import {
  fetchPackagesBegin,
  fetchPackagesSuccess,
  fetchPackagesError,
  fetchProductsBegin,
  fetchProductsSuccess,
  fetchProductsError,
  setActiveSubscription,
  setPremiumSub,
  setActiveProductIds,
  setLoading,
  setPaymentInitiated,
  closeSubscriptionModal,
  fetchHistoryBegin,
  fetchHistorySuccess,
} from "./slice/subscriptionSlice";

// ===== HELPERS =====

const getOperatorUid = () => {
  try {
    const stored = localStorage.getItem("mango_selected_operator");
    if (stored) return JSON.parse(stored).operator_uid;
  } catch {
    // ignore
  }
  return DEFAULT_OPERATOR.operator_uid;
};

const getSubscriberUid = () => {
  // CRM subscriber_uid is the username assigned at signup (e.g. "mango10000021366"),
  // persisted to localStorage by fetchProfile() after each login.
  // Do NOT derive from the phone number the user types into the purchase form.
  const crmUid = localStorage.getItem("mango_subscriber_uid");
  if (crmUid) {
    console.log("[getSubscriberUid] subscriber_uid:", crmUid);
    return crmUid;
  }
  // Fallback: attempt a profile refresh is async, so return the stored username/user_id
  // as a last resort. Callers should refresh profile if this returns null.
  const fallback = localStorage.getItem("mango_username") || localStorage.getItem("mango_user_id");
  console.warn("[getSubscriberUid] falling back (no mango_subscriber_uid):", fallback);
  return fallback;
};

const getUserId = () => localStorage.getItem("mango_user_id");
const getAccessToken = () => localStorage.getItem("mango_access_token");

// ===== 1. FETCH PACKAGES (available plans) =====

/**
 * Fetch available subscription packages.
 *
 * Uses the Client API packages endpoint (same one used for content):
 *   GET /api/client/v1/{operator}/users/{userId}/packages?device_class=desktop
 *
 * This endpoint returns the user's available packages with pricing info.
 * Falls back to CRM /products/ if Client API fails.
 */
export const fetchPackages = async () => {
  const dispatch = store.dispatch;
  dispatch(fetchPackagesBegin());

  try {
    const operatorUid = getOperatorUid();
    const accessToken = getAccessToken();
    const userId = getUserId();

    // Primary: Client API packages (same endpoint used for content fetching)
    if (accessToken && userId) {
      const clientUrl = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`;
      const response = await axios.get(clientUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data.status === "ok" && response.data.data) {
        const packages = response.data.data || [];
        dispatch(fetchPackagesSuccess(packages));
        return packages;
      }
    }

    // Fallback: CRM API products — uses crmRequest helper
    const crmData = await crmRequest("GET", "products/", { operator_uid: operatorUid });

    if (crmData.status === "ok") {
      const packages = crmData.data || [];
      dispatch(fetchPackagesSuccess(packages));
      return packages;
    }

    dispatch(fetchPackagesError("Failed to load packages"));
    return [];
  } catch (error) {
    console.error("fetchPackages error:", error.message);
    dispatch(fetchPackagesError(error.message));
    return [];
  }
};

// ===== 1b. FETCH CRM PRODUCT CATEGORIES =====

/**
 * Fetch product categories from the CRM landing page endpoint.
 * These are the subscription products organized by category.
 *
 * CRM Endpoint: GET /cm/api/client/?operator_uid={operatorUid}&mode=web
 * Response path: data.landing_page.product_categories
 *
 * @returns {Promise<Array>} — array of product category objects
 */
export const fetchCrmProducts = async () => {
  const dispatch = store.dispatch;
  dispatch(fetchProductsBegin());

  try {
    const operatorUid = getOperatorUid();

    // 1. Try cached landing page first (populated by initializeApiConfig)
    let landingPage = getCachedLandingPage();

    // 2. If no cache, fetch and cache now (single shared call)
    if (!landingPage) {
      landingPage = await fetchAndCacheLandingPage(operatorUid);
    }

    // 3. Extract product_categories from landing page
    if (landingPage?.product_categories) {
      const categories = landingPage.product_categories;
      dispatch(fetchProductsSuccess(categories));
      return categories;
    }

    // Landing page exists but no product_categories
    const msg = landingPage
      ? "No product categories in landing page"
      : "Failed to fetch landing page";
    console.warn("fetchCrmProducts:", msg);
    dispatch(fetchProductsError(msg));
    return [];
  } catch (error) {
    console.error("fetchCrmProducts error:", error.message);
    dispatch(fetchProductsError(error.message));
    return [];
  }
};

// ===== 2. PURCHASE PACKAGE =====

// Map gateway id (from the Payment Gateway picker) to the upper-case
// `medium` value the CRM expects. Falls back to "WEB" so older callers
// that don't pass a gateway still work.
const GATEWAY_MEDIUM = {
  flutterwave: "FLUTTERWAVE",
  seerbit: "SEERBIT",
  paystack: "PAYSTACK",
  monnify: "MONNIFY",
};

/**
 * Initiate a subscription purchase via CRM.
 * GloTV pattern: POST /purchase/?operator_uid={op}
 *
 * @param {string|number} productId — CRM product ID
 * @param {string} email — User's email (sourced from onboarding via mango_email)
 * @param {string} phone — User's phone number
 * @param {string} priceOption — Price option uid ("daily" | "weekly" | "monthly" | etc.)
 * @param {string} [gateway] — Selected payment gateway id (flutterwave|seerbit|paystack|monnify)
 * @returns {{ success: boolean, redirectUrl?: string, error?: string }}
 */
export const purchasePackage = async (productId, email, phone, priceOption, gateway) => {
  const dispatch = store.dispatch;
  dispatch(setLoading(true));

  try {
    const operatorUid = getOperatorUid();

    // Ensure we have the CRM-issued `mango_subscriber_uid` before posting.
    // Without it, getSubscriberUid() falls back to the email-style username,
    // which the CRM billing system rejects with 400.
    let subscriberUid = getSubscriberUid();
    if (!subscriberUid || !/^mango/i.test(subscriberUid)) {
      console.log("[purchasePackage] no mango_subscriber_uid — fetching profile to populate");
      await fetchProfile();
      subscriberUid = getSubscriberUid();
    }

    // Persist phone for future CRM calls (history, cancel)
    localStorage.setItem("mango_user_phone", phone);

    // `bill` must be true in live (prod), false in local/dev.
    // NODE_ENV is set by CRA: "production" on `npm run build`, "development" on `npm start`.
    const isLive = process.env.NODE_ENV === "production";

    const medium = GATEWAY_MEDIUM[gateway] || "WEB";

    const body = {
      subscriber_uid: subscriberUid,
      subscription_type: "one-off",
      price_option: priceOption || "",
      bill: isLive,
      product_id: productId,
      medium,
      operator: "",
      billing_option: "",
      email: email,
      phone_number: phone,
      destination_url: `${window.location.origin}/payment-callback`,
    };

    console.log("[purchasePackage] payload:", body);

    // Uses crmRequest helper — routes through serverless function on Vercel
    const responseData = await crmRequest("POST", "purchase/", { operator_uid: operatorUid }, body);

    // Log the full CRM response so the Network tab + console make it clear
    // whether the backend returned a redirect_url (real hosted-gateway flow)
    // or fell through to instant activation (dev `bill: false` path).
    console.log("[purchasePackage] response:", {
      status: responseData?.status,
      hasRedirectUrl: Boolean(
        responseData?.data?.redirect_url || responseData?.redirect_url
      ),
      redirectUrl:
        responseData?.data?.redirect_url || responseData?.redirect_url || null,
      message: responseData?.message,
      billMode: isLive ? "live (will charge)" : "dev (no charge)",
      mediumSent: medium,
      raw: responseData,
    });

    if (responseData.status === "ok") {
      dispatch(setPaymentInitiated(true));

      const redirectUrl = responseData.data?.redirect_url || responseData.redirect_url;

      // If the API returns a redirect URL, the caller handles the redirect
      if (redirectUrl) {
        dispatch(setLoading(false));
        dispatch(closeSubscriptionModal());
        return { success: true, redirectUrl, data: responseData };
      }

      // No redirect — fetch purchase history to update premium status
      await fetchPurchaseHistory("Active");

      dispatch(setLoading(false));
      dispatch(closeSubscriptionModal());

      return { success: true, data: responseData };
    }

    dispatch(setLoading(false));
    return { success: false, error: responseData.message || "Purchase failed" };
  } catch (error) {
    console.error("purchasePackage error:", error.message);
    dispatch(setLoading(false));
    return { success: false, error: error.message };
  }
};

// ===== 3. FETCH PURCHASE HISTORY =====

/**
 * Fetch user's purchase/billing history from CRM.
 * GloTV pattern: GET /orders/?operator_uid={op}&subscriber_uid={sub}&limit=30&status={status}
 *
 * @param {"Active"|"History"} status — Filter by active or historical orders
 */
export const fetchPurchaseHistory = async (status = "Active") => {
  const dispatch = store.dispatch;
  dispatch(fetchHistoryBegin());

  try {
    const operatorUid = getOperatorUid();
    const subscriberUid = getSubscriberUid();

    // When status is empty/"All", fetch every order (active + cancelled + expired)
    // so billing history can display the full record. Active detection still works
    // because we filter by o.status === "Active" below regardless of what came back.
    const queryParams = {
      operator_uid: operatorUid,
      subscriber_uid: subscriberUid,
      limit: "50",
    };
    if (status && status !== "All") queryParams.status = status;

    // Uses crmRequest helper — routes through serverless function on Vercel
    const responseData = await crmRequest("GET", "orders/", queryParams);

    if (responseData.status === "ok") {
      const orders = responseData.data || [];
      dispatch(fetchHistorySuccess(orders));

      // Check if any active subscription exists (with date-range validation)
      const hasActive = checkActiveSubscription(orders);
      dispatch(setPremiumSub(hasActive));

      // Extract active product IDs for granular per-product access checks
      const activeIds = orders
        .filter((o) => o.status === "Active" || o.status === "active")
        .map((o) => String(o.product_id))
        .filter(Boolean);
      dispatch(setActiveProductIds(activeIds));
      console.log("[fetchPurchaseHistory] activeProductIds:", activeIds);

      if (hasActive) {
        // Set the most recent active subscription
        const activeOrder = orders.find(
          (o) => o.status === "Active" || o.status === "active"
        );
        dispatch(setActiveSubscription(activeOrder || orders[0]));
      }

      return orders;
    }

    dispatch(fetchHistorySuccess([]));
    return [];
  } catch (error) {
    console.error("fetchPurchaseHistory error:", error.message);
    dispatch(fetchHistorySuccess([]));
    return [];
  }
};

// ===== 4. CANCEL SUBSCRIPTION =====

/**
 * Cancel an active subscription.
 * GloTV pattern: Two-step process — delete + update via CRM.
 *
 * @param {string|number} productId — Product ID to cancel
 * @param {string} [reason] — Cancellation reason from the survey (optional)
 */
export const cancelSubscription = async (productId, reason) => {
  const dispatch = store.dispatch;
  dispatch(setLoading(true));

  try {
    const operatorUid = getOperatorUid();
    const subscriberUid = getSubscriberUid();

    if (!productId) {
      console.error("[cancelSubscription] No productId provided!");
      dispatch(setLoading(false));
      return { success: false, error: "No product ID" };
    }

    if (!subscriberUid) {
      console.error("[cancelSubscription] No subscriberUid found!");
      dispatch(setLoading(false));
      return { success: false, error: "No subscriber ID" };
    }

    const body = {
      subscriber_uid: subscriberUid,
      product_id: productId,
      medium: "web",
      ...(reason ? { reason } : {}),
    };
    console.log("[cancelSubscription] Body:", body);

    // Step 1: Delete — GloTV pattern: POST /purchase/?operator_uid={op}&method=delete
    const deleteRes = await crmRequest("POST", "purchase/", {
      operator_uid: operatorUid,
      method: "delete",
    }, body);

    if (deleteRes.status === "ok") {
      // Step 2: Update — GloTV pattern: POST /purchase/?operator_uid={op}&method=update
      const updateRes = await crmRequest("POST", "purchase/", {
        operator_uid: operatorUid,
        method: "update",
      }, body);

      if (updateRes.status === "ok") {
        dispatch(setLoading(false));
        dispatch(closeSubscriptionModal());
        dispatch(setActiveSubscription(null));
        dispatch(setPremiumSub(false));

        // Refresh full history so the cancelled order still appears with its
        // updated status (CRM keeps the order record, status just changes).
        await fetchPurchaseHistory("All");

        return { success: true };
      }

      dispatch(setLoading(false));
      return { success: false, error: updateRes.message || "Update step failed" };
    }

    dispatch(setLoading(false));
    return { success: false, error: deleteRes.message || "Delete step failed" };
  } catch (error) {
    console.error("[cancelSubscription] Error:", error.message, error.response?.status, error.response?.data);
    dispatch(setLoading(false));
    return { success: false, error: error.message };
  }
};
